import { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { WorkItemType, TaskPriority, TaskStatus } from '@/types';
import { useHierarchy } from '@/contexts/HierarchyContext';
import {
  useCreateTaskMutation,
  useGetSprintsQuery,
  useGetUsersQuery,
  useGetTaskByIdQuery,
  useGetTasksQuery,
  useAiGenerateDescriptionMutation,
  useAiGenerateAcceptanceCriteriaMutation,
  useAiGenerateBugDetailsMutation,
} from '@/store/api/apiSlice';
import { toast } from 'react-toastify';
import { FiEdit3, FiMaximize2, FiMinimize2, FiPlus, FiX, FiZap } from 'react-icons/fi';
import { actionBtnIconClass, actionBtnOutline, actionBtnPrimary } from '@/utils/actionButtonClasses';
import WorkItemIcon from '@/components/icons/WorkItemIcons';
import SearchableDropdown from '@/components/common/SearchableDropdown';
import RichTextArea from '@/components/common/RichTextArea';
import { getBrowserTimeZone } from '@/utils/dateTime';

interface FullScreenWorkItemCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
  initialType?: WorkItemType;
  parentId?: string;
  projectId?: string;
}

const WORK_ITEM_CONFIG: Record<WorkItemType, {
  bgColor: string; 
  borderColor: string;
  gradient: string;
}> = {
  [WorkItemType.EPIC]: { 
    bgColor: 'bg-orange-50', 
    borderColor: 'border-orange-300',
    gradient: 'from-orange-500 to-orange-600',
  },
  [WorkItemType.FEATURE]: { 
    bgColor: 'bg-blue-50', 
    borderColor: 'border-blue-300',
    gradient: 'from-blue-600 to-blue-700',
  },
  [WorkItemType.USER_STORY]: { 
    bgColor: 'bg-indigo-50', 
    borderColor: 'border-indigo-300',
    gradient: 'from-indigo-600 to-indigo-700',
  },
  [WorkItemType.TASK]: { 
    bgColor: 'bg-green-50', 
    borderColor: 'border-green-300',
    gradient: 'from-green-600 to-green-700',
  },
  [WorkItemType.BUG]: { 
    bgColor: 'bg-red-50', 
    borderColor: 'border-red-300',
    gradient: 'from-red-600 to-red-700',
  },
};

const TYPE_COLORS: Record<WorkItemType, string> = {
  [WorkItemType.FEATURE]: 'bg-blue-100 text-blue-800',
  [WorkItemType.USER_STORY]: 'bg-indigo-100 text-indigo-800',
  [WorkItemType.TASK]: 'bg-green-100 text-green-800',
  [WorkItemType.BUG]: 'bg-red-100 text-red-800',
  [WorkItemType.EPIC]: 'bg-orange-100 text-orange-800',
};

const TYPE_BADGE: Record<string, { bg: string; text: string }> = {
  [WorkItemType.EPIC]: { bg: 'bg-orange-600', text: 'text-white' },
  [WorkItemType.FEATURE]: { bg: 'bg-purple-700', text: 'text-white' },
  [WorkItemType.USER_STORY]: { bg: 'bg-blue-600', text: 'text-white' },
  [WorkItemType.TASK]: { bg: 'bg-yellow-500', text: 'text-gray-900' },
  [WorkItemType.BUG]: { bg: 'bg-red-600', text: 'text-white' },
};

const STATUS_COLORS: Record<string, string> = {
  [TaskStatus.TODO]: 'bg-gray-400',
  [TaskStatus.IN_PROGRESS]: 'bg-blue-500',
  [TaskStatus.IN_REVIEW]: 'bg-orange-500',
  [TaskStatus.DONE]: 'bg-green-500',
  [TaskStatus.BLOCKED]: 'bg-red-500',
};

/* DevOps-style input class constants */
const devopsInputCls = 'w-full px-2 py-1 border border-border rounded-[3px] text-[12px] text-text-primary bg-surface hover:border-brand focus:border-brand focus:outline-none transition-colors';
const devopsSelectCls = `${devopsInputCls} cursor-pointer`;
const devopsTextareaCls = 'w-full px-2 py-1.5 border border-border rounded-[3px] text-[12px] text-text-primary bg-surface hover:border-brand focus:border-brand focus:outline-none transition-colors resize-none';
const devopsLabelCls = 'block text-[11px] font-semibold text-text-muted uppercase tracking-wider mb-1';

export default function FullScreenWorkItemCreator({
  isOpen,
  onClose,
  onSave,
  initialType,
  parentId,
  projectId: propProjectId,
}: FullScreenWorkItemCreatorProps) {
  const { t } = useTranslation();
  const { isAllowed, descriptor, childTypesFor, parentTypeFor } = useHierarchy();
  const browserTimeZone = useMemo(() => getBrowserTimeZone(), []);
  const [activeTab, setActiveTab] = useState<'form' | 'ai'>('form');
  const [aiPrompt, setAiPrompt] = useState('');
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsMaximized(false);
    }
  }, [isOpen]);
  
  const [formData, setFormData] = useState({
    type: (initialType && isAllowed(initialType))
      ? initialType
      : (isAllowed(WorkItemType.TASK) ? WorkItemType.TASK : (descriptor.allowedTypes[0] ?? WorkItemType.TASK)),
    title: '',
    description: '',
    acceptanceCriteria: '',
    projectId: propProjectId || '',
    sprintId: '',
    assigneeId: '',
    selectedParentId: parentId || '',
    priority: TaskPriority.MEDIUM,
    status: TaskStatus.TODO,
    estimatedHours: '',
    dueDate: '',
    tags: '',
    // Feature-specific fields
    businessValue: '',
    targetRelease: '',
    riskAssessment: '',
    dependencies: '',
    // User Story-specific fields
    storyPoints: '',
    persona: '',
    userNotes: '',
    // Bug-specific fields
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: '',
    environment: '',
    severity: '',
    // Task-specific fields
    taskCategory: '',
    technicalDetails: '',
    complexity: '',
    blockers: '',
    // Epic-specific fields
    epicRisk: '',
    epicValueArea: '',
    epicTimeCriticality: '',
    epicEffort: '',
  });

  const { data: users = [] } = useGetUsersQuery({ limit: 100 });
  const { data: sprints = [] } = useGetSprintsQuery(formData.projectId, {
    skip: !formData.projectId,
  });

  // Determine required parent type based on current work item type and the
  // tenant's hierarchy mode (e.g. FULL: Story→Feature, Feature→Epic).
  const requiredParentType = parentTypeFor(formData.type);

  const requiresParent = requiredParentType !== null;

  // Query for potential parent tasks
  const { data: potentialParents = [] } = useGetTasksQuery(
    { 
      projectId: formData.projectId, 
      type: requiredParentType,
      limit: 100 // Ensure we get all potential parents
    },
    { skip: !formData.projectId || !requiredParentType }
  );

  // Fetch parent task to show hierarchy context
  const { data: parentTask } = useGetTaskByIdQuery(formData.selectedParentId || '', {
    skip: !formData.selectedParentId,
  });

  // Fetch initial parent if parentId prop is provided
  const { data: initialParentTask } = useGetTaskByIdQuery(parentId || '', {
    skip: !parentId,
  });

  // Determine allowed child types based on parent and the tenant's hierarchy mode.
  // null  = no parent → any allowed type for the mode
  // []    = parent exists but cannot have children in this mode (e.g. Feature in LEAN)
  // [...] = restricted to those child types
  const allowedChildTypes = useMemo(() => {
    const parent = initialParentTask || parentTask;
    if (!parent) return null;
    return childTypesFor(parent.type as WorkItemType);
  }, [initialParentTask, parentTask, childTypesFor]);
  const parentBlocksChildren = Array.isArray(allowedChildTypes) && allowedChildTypes.length === 0;

  const [createTask, { isLoading: isSaving }] = useCreateTaskMutation();
  const [aiGenerateDescription] = useAiGenerateDescriptionMutation();
  const [aiGenerateAcceptanceCriteria, { isLoading: isGeneratingAC }] = useAiGenerateAcceptanceCriteriaMutation();
  const [aiGenerateBugDetails] = useAiGenerateBugDetailsMutation();

  const userOptions = useMemo(() => users || [], [users]);
  const sprintOptions = useMemo(() => (Array.isArray(sprints) ? sprints : []), [sprints]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, initialType, propProjectId]);

  // Set initial type based on parent when creating child items
  useEffect(() => {
    if (isOpen && allowedChildTypes && allowedChildTypes.length > 0) {
      // Auto-select the first (or only) allowed child type
      setFormData((prev) => ({ ...prev, type: allowedChildTypes[0] }));
    }
  }, [isOpen, allowedChildTypes]);

  // Keep parent/project context pinned when opening from a parent item.
  // Never force a parent onto top-level types (Epic / Feature depending on mode).
  useEffect(() => {
    if (!isOpen || !parentId || !initialParentTask) return;
    setFormData((prev) => {
      const needsParent = parentTypeFor(prev.type) !== null;
      return {
        ...prev,
        projectId: prev.projectId || initialParentTask.projectId,
        selectedParentId: needsParent ? (prev.selectedParentId || parentId) : '',
      };
    });
  }, [isOpen, parentId, initialParentTask, parentTypeFor]);

  const effectiveParentId = (formData.selectedParentId || parentId || '').trim();

  // When switching type, preserve shared fields and clear all type-specific fields
  const handleTypeChange = (newType: WorkItemType) => {
    const nextRequiresParent = parentTypeFor(newType) !== null;
    setFormData((prev) => ({
      ...prev,
      type: newType,
      // Top-level types (e.g. Epic in Lean) must not keep a parent from a prior selection.
      selectedParentId: nextRequiresParent ? prev.selectedParentId : '',
      // Feature-specific
      businessValue: '',
      targetRelease: '',
      riskAssessment: '',
      dependencies: '',
      // User Story-specific
      storyPoints: '',
      persona: '',
      userNotes: '',
      // Bug-specific
      stepsToReproduce: '',
      expectedBehavior: '',
      actualBehavior: '',
      environment: '',
      severity: '',
      // Task-specific
      taskCategory: '',
      technicalDetails: '',
      complexity: '',
      blockers: '',
      // Epic-specific
      epicRisk: '',
      epicValueArea: '',
      epicTimeCriticality: '',
      epicEffort: '',
      // Shared but type-scoped (AC only applies to Feature/User Story/Epic)
      acceptanceCriteria: '',
    }));
  };

  const handleCreate = async () => {
    if (parentBlocksChildren) {
      toast.error(
        t('workItemCreator.errors.parentCannotHaveChildren', {
          defaultValue:
            'This parent type cannot have children under the current organization hierarchy. Create from Backlog without that parent, or change hierarchy in Org Settings.',
        }),
      );
      return;
    }
    if (!formData.title.trim()) {
      toast.error(t('workItemCreator.errors.titleRequired'));
      return;
    }
    if (!formData.description.trim()) {
      toast.error(t('workItemCreator.errors.descriptionRequired'));
      return;
    }
    if (!formData.projectId) {
      toast.error(t('workItemCreator.errors.projectRequired'));
      return;
    }
    if ((formData.type === WorkItemType.TASK || formData.type === WorkItemType.BUG) && formData.estimatedHours.trim() && Number(formData.estimatedHours) < 0) {
      toast.error(t('workItemCreator.errors.estimatedHours'));
      return;
    }

    // Check if due date is in the past
    if (formData.dueDate) {
      const dueDate = new Date(formData.dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dueDate < today) {
        toast.warn(t('workItemCreator.errors.dueDatePast'));
      }
    }

    try {
      const tagsArray = formData.tags
        ? formData.tags
            .split(',')
            .map((t) => t.trim())
            .filter((t) => t.length > 0)
        : [];

      // Prepare type-specific metadata
      let metadata: any = {};
      
      if (formData.type === WorkItemType.FEATURE) {
        metadata = {
          businessValue: formData.businessValue || null,
          targetRelease: formData.targetRelease || null,
          riskLevel: formData.riskAssessment || null,
          dependencies: formData.dependencies || null,
        };
      } else if (formData.type === WorkItemType.USER_STORY) {
        metadata = {
          acceptanceCriteria: formData.acceptanceCriteria || null,
          userPersona: formData.persona || null,
          storyPoints: formData.storyPoints || null,
        };
      } else if (formData.type === WorkItemType.TASK) {
        metadata = {
          taskCategory: formData.taskCategory || null,
          complexity: formData.complexity || null,
          technicalDetails: formData.technicalDetails || null,
        };
      } else if (formData.type === WorkItemType.BUG) {
        metadata = {
          stepsToReproduce: formData.stepsToReproduce || null,
          expectedBehavior: formData.expectedBehavior || null,
          actualBehavior: formData.actualBehavior || null,
          severity: formData.severity || null,
          environment: formData.environment || null,
        };
      } else if (formData.type === WorkItemType.EPIC) {
        metadata = {
          businessValue: formData.businessValue || null,
          risk: formData.epicRisk || null,
          valueArea: formData.epicValueArea || null,
          timeCriticality: formData.epicTimeCriticality ? Number(formData.epicTimeCriticality) : null,
          effort: formData.epicEffort ? Number(formData.epicEffort) : null,
        };
      }

      await createTask({
        title: formData.title,
        description: formData.description,
        type: formData.type,
        projectId: formData.projectId,
        sprintId: formData.sprintId || undefined,
        assigneeId: formData.assigneeId || undefined,
        // Never send parentId for top-level types (Epic in Lean/Full) — API rejects it.
        parentId: requiresParent ? (effectiveParentId || undefined) : undefined,
        acceptanceCriteria: formData.acceptanceCriteria || undefined,
        priority: formData.priority,
        status: formData.status,
        estimatedHours: formData.estimatedHours ? Number(formData.estimatedHours) : undefined,
        dueDate: formData.dueDate || undefined,
        tags: tagsArray.length > 0 ? tagsArray : undefined,
        metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
      }).unwrap();

      if (onSave) {
        onSave();
      } else {
        toast.success(t('workItemCreator.created'));
        onClose();
      }
    } catch (error: any) {
      const apiError =
        error?.data?.error ||
        error?.data?.message ||
        (Array.isArray(error?.data?.errors) ? error.data.errors.map((e: any) => e.msg || e).join(', ') : null) ||
        error?.message;
      toast.error(apiError || t('workItemCreator.errors.createFailed'));
    }
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast.error(t('workItemCreator.errors.aiPromptRequired'));
      return;
    }

    const currentType = formData.type;
    const needsAC = currentType === WorkItemType.FEATURE || currentType === WorkItemType.USER_STORY;
    const isBug = currentType === WorkItemType.BUG;

    try {
      const [descResult, acResult, bugResult] = await Promise.all([
        aiGenerateDescription({ title: aiPrompt, type: currentType }).unwrap().catch(() => null),
        needsAC
          ? aiGenerateAcceptanceCriteria({ title: aiPrompt, description: '' }).unwrap().catch(() => null)
          : Promise.resolve(null),
        isBug
          ? aiGenerateBugDetails({ title: aiPrompt }).unwrap().catch(() => null)
          : Promise.resolve(null),
      ]);

      const generatedTitle = aiPrompt.charAt(0).toUpperCase() + aiPrompt.slice(1);
      const generatedDesc = descResult?.description || t('workItemCreator.generatedDescription', { prompt: aiPrompt });

      setFormData((prev) => {
        const base = {
          ...prev,
          title: generatedTitle,
          description: generatedDesc,
        };

        if (currentType === WorkItemType.FEATURE || currentType === WorkItemType.USER_STORY) {
          return { ...base, acceptanceCriteria: acResult?.acceptanceCriteria || prev.acceptanceCriteria };
        }

        if (currentType === WorkItemType.BUG) {
          return {
            ...base,
            stepsToReproduce: bugResult?.stepsToReproduce || '1. \n2. \n3. ',
            expectedBehavior: bugResult?.expectedBehavior || prev.expectedBehavior,
            actualBehavior: bugResult?.actualBehavior || prev.actualBehavior,
          };
        }

        return base;
      });

      setActiveTab('form');
      toast.success(t('workItemCreator.aiGenerated'));
    } catch {
      toast.error(t('workItemCreator.errors.aiGenerationFailed'));
    }
  };

  const resetForm = () => {
    const safeType =
      initialType && isAllowed(initialType)
        ? initialType
        : isAllowed(WorkItemType.TASK)
          ? WorkItemType.TASK
          : (descriptor.allowedTypes[0] ?? WorkItemType.TASK);
    const typeNeedsParent = parentTypeFor(safeType) !== null;
    setFormData({
      type: safeType,
      title: '',
      description: '',
      acceptanceCriteria: '',
      projectId: propProjectId || '',
      sprintId: '',
      assigneeId: '',
      // Don't pin a parent onto top-level types (Epic in Lean/Full, Feature in Feature-First).
      selectedParentId: typeNeedsParent ? parentId || '' : '',
      priority: TaskPriority.MEDIUM,
      status: TaskStatus.TODO,
      estimatedHours: '',
      dueDate: '',
      tags: '',
      businessValue: '',
      targetRelease: '',
      riskAssessment: '',
      dependencies: '',
      storyPoints: '',
      persona: '',
      userNotes: '',
      stepsToReproduce: '',
      expectedBehavior: '',
      actualBehavior: '',
      environment: '',
      severity: '',
      taskCategory: '',
      technicalDetails: '',
      complexity: '',
      blockers: '',
      epicRisk: '',
      epicValueArea: '',
      epicTimeCriticality: '',
      epicEffort: '',
    });
    setAiPrompt('');
    setActiveTab('form');
  };

  if (!isOpen) return null;

  const workItemLabel = (type: WorkItemType) => t(`workItem.types.${type}.label`);
  const selectedAssignee = userOptions.find((u) => u.id === formData.assigneeId);
  const sprintSelectOptions = [{ value: '', label: t('workItemCreator.backlog') }, ...sprintOptions.map((sprint: any) => ({ value: sprint.id, label: sprint.name, description: sprint.goal || sprint.status }))];
  const requiredParentLabel = requiredParentType
    ? workItemLabel(requiredParentType)
    : '';
  const parentSelectOptions = [{
    value: '',
    label: t('workItemCreator.selectParent', { type: requiredParentLabel.toLowerCase() || t('workItemCreator.parent') }),
  }, ...potentialParents.map((parent: any) => ({ value: parent.id, label: parent.title, description: parent.status }))];
  const assigneeSelectOptions = [{ value: '', label: t('backlogPage.filterUnassigned') }, ...userOptions.map((user) => ({ value: user.id, label: `${user.firstName} ${user.lastName}`, description: user.role }))];
  const statusSelectOptions = Object.values(TaskStatus).map((status) => ({
    value: status,
    label: t(`workItemCreator.status.${status}`, {
      defaultValue: t(`taskStatus.${status}`, { defaultValue: status.replace(/_/g, ' ') }),
    }),
  }));
  const prioritySelectOptions = Object.values(TaskPriority).map((priority) => ({
    value: priority,
    label: t(`workItemCreator.priority.${priority}`, { defaultValue: priority }),
  }));
  
  // Validation: title, description, project required; parent must allow children when set.
  const isFormValid = Boolean(
    formData.title.trim() &&
    formData.description.trim() &&
    formData.projectId &&
    !parentBlocksChildren
  );
  
  const badge = TYPE_BADGE[formData.type] || TYPE_BADGE[WorkItemType.TASK];

  // Get parent context for display
  const displayParent = initialParentTask || parentTask;
  const parentContext = displayParent 
    ? t('workItemCreator.underParent', { type: workItemLabel(displayParent.type), title: displayParent.title })
    : null;

  const creator = (
    <div className="fixed inset-0 z-overlay flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        className="bg-surface border border-border flex flex-col shadow-2xl overflow-hidden"
        style={{
          width: isMaximized ? '100vw' : '92vw',
          maxWidth: isMaximized ? '100vw' : '1200px',
          height: isMaximized ? '100vh' : '85vh',
          borderRadius: isMaximized ? '0' : '8px',
        }}
      >

        {/* ── HEADER BAR ── */}
        <div className="flex items-center justify-between px-5 py-2 border-b border-border bg-surface min-h-[40px] flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-[3px] ${badge.bg} ${badge.text}`}>
              <WorkItemIcon type={formData.type} size={14} className="text-current" />
              <span className="text-xs font-semibold tracking-wide">{t('workItemCreator.newType', { type: workItemLabel(formData.type) })}</span>
            </div>
            <span className="text-[13px] text-text-muted">
              {parentContext || t(`workItem.types.${formData.type}.description`)}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMaximized((prev) => !prev)}
              className="p-1.5 text-text-muted hover:text-text-primary hover:bg-surface-hover rounded transition-colors"
              title={isMaximized ? t('workItemCreator.restore') : t('workItemCreator.maximize')}
            >
              {isMaximized ? <FiMinimize2 className="w-4 h-4" /> : <FiMaximize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-text-muted hover:text-text-primary hover:bg-surface-hover rounded transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* ── TYPE SELECTOR + TABS ── */}
        <div className="px-6 pt-3 pb-0 flex-shrink-0">
          {parentBlocksChildren && (
            <div className="mb-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
              This parent type cannot have children under the current organization hierarchy.
              Close and create from Backlog without that parent, or adjust Work Item Hierarchy in Org Settings.
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {Object.entries(WORK_ITEM_CONFIG)
                .filter(([type]) => isAllowed(type as WorkItemType))
                .map(([type]) => {
                const isHierarchyAllowed = !allowedChildTypes || allowedChildTypes.includes(type as WorkItemType);
                const isDisabled = allowedChildTypes && !isHierarchyAllowed;
                
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => !isDisabled && handleTypeChange(type as WorkItemType)}
                    disabled={isDisabled}
                    title={isDisabled ? t('workItemCreator.cannotCreateUnder', { type: workItemLabel(type as WorkItemType), parent: workItemLabel((initialParentTask?.type || parentTask?.type) as WorkItemType) }) : undefined}
                    className={`flex items-center gap-1 px-2 py-0.5 rounded-[3px] text-[12px] font-medium transition-colors ${
                      formData.type === type
                        ? `${TYPE_BADGE[type as WorkItemType]?.bg} ${TYPE_BADGE[type as WorkItemType]?.text}`
                        : isDisabled
                        ? 'text-text-muted bg-surface-hover cursor-not-allowed'
                        : 'text-text-secondary hover:bg-surface-hover'
                    }`}
                  >
                    <WorkItemIcon type={type as WorkItemType} size={12} />
                    {workItemLabel(type as WorkItemType)}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setActiveTab('form')}
                className={`pb-2 px-1 text-[12px] font-medium border-b-2 transition-colors ${
                  activeTab === 'form'
                    ? 'border-brand text-brand'
                    : 'border-transparent text-text-muted hover:text-text-secondary'
                }`}
              >
                <span className="flex items-center gap-1.5"><FiEdit3 className="w-3.5 h-3.5" />{t('workItemCreator.tabs.form')}</span>
              </button>
              <button
                onClick={() => setActiveTab('ai')}
                className={`pb-2 px-1 text-[12px] font-medium border-b-2 transition-colors ${
                  activeTab === 'ai'
                    ? 'border-brand text-brand'
                    : 'border-transparent text-text-muted hover:text-text-secondary'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <FiZap className="w-3.5 h-3.5" />
                  <span>{t('header.aiAssistant')}</span>
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] leading-none font-semibold bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-600 text-white border border-blue-700">
                    AI
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>
        <div className="border-b border-border" />

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 bg-bg">
          {activeTab === 'ai' ? (
            <div className="space-y-4">
              {/* AI Prompt Input */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-brand"></div>
                  <label className="text-[12px] font-semibold text-text-primary">{t('workItemCreator.aiPromptLabel')}</label>
                </div>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder={t('workItemCreator.aiPromptPlaceholder')}
                  rows={6}
                  className={devopsTextareaCls}
                />
              </div>

              {/* Type Selector */}
              <div className="flex items-center gap-3">
                <select
                  value={formData.type}
                  onChange={(e) => handleTypeChange(e.target.value as WorkItemType)}
                  className="px-3 py-1.5 border border-border rounded-[3px] bg-surface text-text-primary hover:border-brand focus:border-brand outline-none text-[12px] font-medium"
                >
                  {Object.entries(WORK_ITEM_CONFIG)
                    .filter(([type]) => isAllowed(type as WorkItemType))
                    .map(([type, cfg]) => (
                    <option key={type} value={type}>
                      {workItemLabel(type as WorkItemType)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleAiGenerate}
                  disabled={!aiPrompt.trim() || isGeneratingAC}
                  className={`${actionBtnPrimary} flex-1`}
                >
                  <FiZap className={actionBtnIconClass} aria-hidden />
                  {isGeneratingAC ? t('workItemCreator.generating') : t('workItemCreator.generate')}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-5">
              {/* Left Column - Main Form */}
              <div className="flex-1 space-y-3">

                {/* Title */}
                <div>
                  <label className={devopsLabelCls}>
                    {t('workItemCreator.title')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder={t('workItemCreator.titlePlaceholder')}
                    className={devopsInputCls}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className={devopsLabelCls}>
                    {t('workItemCreator.description')} <span className="text-red-500">*</span>
                  </label>
                  <RichTextArea
                    value={formData.description}
                    onChange={(v) => setFormData((prev) => ({ ...prev, description: v }))}
                    placeholder={t('workItemCreator.descriptionPlaceholder')}
                    rows={6}
                    className={devopsTextareaCls}
                  />
                </div>

                {/* Type-Specific Sections */}
                {formData.type === WorkItemType.EPIC && (
                  <>
                    <div className="rounded-[3px] border border-border bg-surface p-3 space-y-3">
                      <h3 className="text-[11px] font-bold text-text-secondary uppercase tracking-wider flex items-center gap-2">
                        <span className="text-orange-600">⚡</span>
                        {t('workItemCreator.epicPlanning')}
                      </h3>
                      <div>
                        <label className={devopsLabelCls}>{t('workItemCreator.businessValue')}</label>
                        <textarea
                          value={formData.businessValue}
                          onChange={(e) => setFormData((prev) => ({ ...prev, businessValue: e.target.value }))}
                          placeholder={t('workItemCreator.businessValueEpicPlaceholder')}
                          rows={3}
                          className={devopsTextareaCls}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={devopsLabelCls}>{t('workItemCreator.valueArea')}</label>
                          <select
                            value={formData.epicValueArea}
                            onChange={(e) => setFormData((prev) => ({ ...prev, epicValueArea: e.target.value }))}
                            className={devopsSelectCls}
                          >
                            <option value="">{t('workItemCreator.selectArea')}</option>
                            <option value="Business">{t('workItemCreator.valueAreas.business')}</option>
                            <option value="Architectural">{t('workItemCreator.valueAreas.architectural')}</option>
                          </select>
                        </div>
                        <div>
                          <label className={devopsLabelCls}>{t('workItemCreator.risk')}</label>
                          <select
                            value={formData.epicRisk}
                            onChange={(e) => setFormData((prev) => ({ ...prev, epicRisk: e.target.value }))}
                            className={devopsSelectCls}
                          >
                            <option value="">{t('workItemCreator.selectRisk')}</option>
                            <option value="LOW">{t('workItemCreator.riskLevels.low')}</option>
                            <option value="MEDIUM">{t('workItemCreator.riskLevels.medium')}</option>
                            <option value="HIGH">{t('workItemCreator.riskLevels.high')}</option>
                            <option value="CRITICAL">{t('workItemCreator.riskLevels.critical')}</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={devopsLabelCls}>{t('workItemCreator.timeCriticality')}</label>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={formData.epicTimeCriticality}
                            onChange={(e) => setFormData((prev) => ({ ...prev, epicTimeCriticality: e.target.value }))}
                            placeholder={t('workItemCreator.timeCriticalityPlaceholder')}
                            className={devopsInputCls}
                          />
                        </div>
                        <div>
                          <label className={devopsLabelCls}>{t('workItemCreator.effortStoryPoints')}</label>
                          <select
                            value={formData.epicEffort}
                            onChange={(e) => setFormData((prev) => ({ ...prev, epicEffort: e.target.value }))}
                            className={devopsSelectCls}
                          >
                            <option value="">{t('workItemCreator.selectPoints')}</option>
                            {[1, 2, 3, 5, 8, 13, 21, 40, 100].map((p) => (
                              <option key={p} value={p.toString()}>{p}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-[3px] border border-border bg-surface p-3">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-[11px] font-bold text-text-secondary uppercase tracking-wider flex items-center gap-2">
                          <span className="text-amber-600">✓</span>
                          {t('workItemCreator.acceptanceCriteria')}
                        </h3>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!formData.title.trim() && !formData.description.trim()) {
                              toast.error(t('workItemCreator.errors.titleOrDescriptionRequired'));
                              return;
                            }
                            try {
                              const result = await aiGenerateAcceptanceCriteria({
                                title: formData.title || formData.description,
                                description: formData.description,
                              }).unwrap();
                              setFormData((prev) => ({ ...prev, acceptanceCriteria: result.acceptanceCriteria }));
                            } catch {
                              toast.error(t('workItemCreator.errors.acceptanceCriteriaFailed'));
                            }
                          }}
                          disabled={isGeneratingAC}
                          className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-brand border border-brand rounded-[3px] hover:bg-surface-hover disabled:opacity-50 transition-colors"
                        >
                          <FiZap className="w-2.5 h-2.5" />
                          {isGeneratingAC ? t('workItemCreator.generating') : t('workItemCreator.generateWithAI')}
                        </button>
                      </div>
                      <RichTextArea
                        value={formData.acceptanceCriteria}
                        onChange={(v) => setFormData((prev) => ({ ...prev, acceptanceCriteria: v }))}
                        placeholder={t('workItemCreator.acceptanceCriteriaEpicPlaceholder')}
                        rows={6}
                        className={devopsTextareaCls}
                      />
                    </div>
                  </>
                )}
                {formData.type === WorkItemType.FEATURE && (
                  <>
                    <div className="rounded-[3px] border border-border bg-surface p-3 space-y-3">
                      <h3 className="text-[11px] font-bold text-text-secondary uppercase tracking-wider flex items-center gap-2">
                        <span className="text-amber-600">✦</span>
                        {t('workItemCreator.featurePlanning')}
                      </h3>
                      <div>
                        <label className={devopsLabelCls}>{t('workItemCreator.businessValue')}</label>
                        <textarea
                          value={formData.businessValue}
                          onChange={(e) => setFormData((prev) => ({ ...prev, businessValue: e.target.value }))}
                          placeholder={t('workItemCreator.businessValueFeaturePlaceholder')}
                          rows={3}
                          className={devopsTextareaCls}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={devopsLabelCls}>{t('workItemCreator.targetRelease')}</label>
                          <input
                            type="text"
                            value={formData.targetRelease}
                            onChange={(e) => setFormData((prev) => ({ ...prev, targetRelease: e.target.value }))}
                            placeholder={t('workItemCreator.targetReleasePlaceholder')}
                            className={devopsInputCls}
                          />
                        </div>
                        <div>
                          <label className={devopsLabelCls}>{t('workItemCreator.riskLevel')}</label>
                          <select
                            value={formData.riskAssessment}
                            onChange={(e) => setFormData((prev) => ({ ...prev, riskAssessment: e.target.value }))}
                            className={devopsSelectCls}
                          >
                            <option value="">{t('workItemCreator.selectRisk')}</option>
                            <option value="low">{t('workItemCreator.riskLevels.lowRisk')}</option>
                            <option value="medium">{t('workItemCreator.riskLevels.mediumRisk')}</option>
                            <option value="high">{t('workItemCreator.riskLevels.highRisk')}</option>
                          </select>
                        </div>
                      </div>

                    </div>
                    <div className="rounded-[3px] border border-border bg-surface p-3">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-[11px] font-bold text-text-secondary uppercase tracking-wider flex items-center gap-2">
                          <span className="text-amber-600">✓</span>
                          {t('workItemCreator.acceptanceCriteria')}
                        </h3>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!formData.title.trim() && !formData.description.trim()) {
                              toast.error(t('workItemCreator.errors.titleOrDescriptionRequired'));
                              return;
                            }
                            try {
                              const result = await aiGenerateAcceptanceCriteria({
                                title: formData.title || formData.description,
                                description: formData.description,
                              }).unwrap();
                              setFormData((prev) => ({ ...prev, acceptanceCriteria: result.acceptanceCriteria }));
                            } catch {
                              toast.error(t('workItemCreator.errors.acceptanceCriteriaFailed'));
                            }
                          }}
                          disabled={isGeneratingAC}
                          className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-brand border border-brand rounded-[3px] hover:bg-surface-hover disabled:opacity-50 transition-colors"
                        >
                          <FiZap className="w-2.5 h-2.5" />
                          {isGeneratingAC ? t('workItemCreator.generating') : t('workItemCreator.generateWithAI')}
                        </button>
                      </div>
                      <RichTextArea
                        value={formData.acceptanceCriteria}
                        onChange={(v) => setFormData((prev) => ({ ...prev, acceptanceCriteria: v }))}
                        placeholder={t('workItemCreator.acceptanceCriteriaFeaturePlaceholder')}
                        rows={6}
                        className={devopsTextareaCls}
                      />
                    </div>
                  </>
                )}
                {formData.type === WorkItemType.USER_STORY && (
                  <>
                    <div className="rounded-[3px] border border-border bg-surface p-3 space-y-3">
                      <h3 className="text-[11px] font-bold text-text-secondary uppercase tracking-wider flex items-center gap-2">
                        <span className="text-blue-600">👤</span>
                        {t('workItemCreator.userStoryDetails')}
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={devopsLabelCls}>{t('workItemCreator.userPersona')}</label>
                          <input
                            type="text"
                            value={formData.persona}
                            onChange={(e) => setFormData((prev) => ({ ...prev, persona: e.target.value }))}
                            placeholder={t('workItemCreator.userPersonaPlaceholder')}
                            className={devopsInputCls}
                          />
                        </div>
                        <div>
                          <label className={devopsLabelCls}>{t('backlogPage.columns.storyPoints')}</label>
                          <select
                            value={formData.storyPoints}
                            onChange={(e) => setFormData((prev) => ({ ...prev, storyPoints: e.target.value }))}
                            className={devopsSelectCls}
                          >
                            <option value="">{t('workItemCreator.selectPoints')}</option>
                            {[1, 2, 3, 5, 8, 13, 21].map((points) => (
                              <option key={points} value={points.toString()}>{points}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                    </div>
                    <div className="rounded-[3px] border border-border bg-surface p-3">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-[11px] font-bold text-text-secondary uppercase tracking-wider flex items-center gap-2">
                          <span className="text-amber-600">✓</span>
                          {t('workItemCreator.acceptanceCriteria')}
                        </h3>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!formData.title.trim() && !formData.description.trim()) {
                              toast.error(t('workItemCreator.errors.titleOrDescriptionRequired'));
                              return;
                            }
                            try {
                              const result = await aiGenerateAcceptanceCriteria({
                                title: formData.title || formData.description,
                                description: formData.description,
                              }).unwrap();
                              setFormData((prev) => ({ ...prev, acceptanceCriteria: result.acceptanceCriteria }));
                            } catch {
                              toast.error(t('workItemCreator.errors.acceptanceCriteriaFailed'));
                            }
                          }}
                          disabled={isGeneratingAC}
                          className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-brand border border-brand rounded-[3px] hover:bg-surface-hover disabled:opacity-50 transition-colors"
                        >
                          <FiZap className="w-2.5 h-2.5" />
                          {isGeneratingAC ? t('workItemCreator.generating') : t('workItemCreator.generateWithAI')}
                        </button>
                      </div>
                      <RichTextArea
                        value={formData.acceptanceCriteria}
                        onChange={(v) => setFormData((prev) => ({ ...prev, acceptanceCriteria: v }))}
                        placeholder={t('workItemCreator.acceptanceCriteriaStoryPlaceholder')}
                        rows={6}
                        className={devopsTextareaCls}
                      />
                    </div>
                  </>
                )}
                {formData.type === WorkItemType.TASK && (
                  <div className="rounded-[3px] border border-border bg-surface p-3 space-y-3">
                    <h3 className="text-[11px] font-bold text-text-secondary uppercase tracking-wider flex items-center gap-2">
                      <span className="text-yellow-600">⚙</span>
                      {t('workItemCreator.taskDetails')}
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={devopsLabelCls}>{t('workItemCreator.taskCategory')}</label>
                        <select
                          value={formData.taskCategory}
                          onChange={(e) => setFormData((prev) => ({ ...prev, taskCategory: e.target.value }))}
                          className={devopsSelectCls}
                        >
                          <option value="">{t('workItemCreator.selectCategory')}</option>
                          <option value="development">{t('workItemCreator.categories.development')}</option>
                          <option value="testing">{t('workItemCreator.categories.testing')}</option>
                          <option value="documentation">{t('workItemCreator.categories.documentation')}</option>
                          <option value="deployment">{t('workItemCreator.categories.deployment')}</option>
                          <option value="research">{t('workItemCreator.categories.research')}</option>
                        </select>
                      </div>
                      <div>
                        <label className={devopsLabelCls}>{t('workItemCreator.complexity')}</label>
                        <select
                          value={formData.complexity}
                          onChange={(e) => setFormData((prev) => ({ ...prev, complexity: e.target.value }))}
                          className={devopsSelectCls}
                        >
                          <option value="">{t('workItemCreator.selectComplexity')}</option>
                          <option value="simple">{t('workItemCreator.complexities.simple')}</option>
                          <option value="moderate">{t('workItemCreator.complexities.moderate')}</option>
                          <option value="complex">{t('workItemCreator.complexities.complex')}</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className={devopsLabelCls}>{t('workItemCreator.technicalNotes')}</label>
                      <textarea
                        value={formData.technicalDetails}
                        onChange={(e) => setFormData((prev) => ({ ...prev, technicalDetails: e.target.value }))}
                        placeholder={t('workItemCreator.technicalNotesPlaceholder')}
                        rows={4}
                        className={devopsTextareaCls}
                      />
                    </div>
                  </div>
                )}
                {formData.type === WorkItemType.BUG && (
                  <div className="rounded-[3px] border border-border bg-surface p-3 space-y-3">
                    <h3 className="text-[11px] font-bold text-text-secondary uppercase tracking-wider flex items-center gap-2">
                      <span className="text-red-600">⚠</span>
                      {t('workItemCreator.bugDetails')}
                    </h3>
                    <div>
                      <label className={devopsLabelCls}>{t('workItemCreator.stepsToReproduce')}</label>
                      <textarea
                        value={formData.stepsToReproduce}
                        onChange={(e) => setFormData((prev) => ({ ...prev, stepsToReproduce: e.target.value }))}
                        placeholder={t('workItemCreator.stepsToReproducePlaceholder')}
                        rows={3}
                        className={devopsTextareaCls}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={devopsLabelCls}>{t('workItemCreator.expectedBehavior')}</label>
                        <textarea
                          value={formData.expectedBehavior}
                          onChange={(e) => setFormData((prev) => ({ ...prev, expectedBehavior: e.target.value }))}
                          placeholder={t('workItemCreator.expectedBehaviorPlaceholder')}
                          rows={2}
                          className={devopsTextareaCls}
                        />
                      </div>
                      <div>
                        <label className={devopsLabelCls}>{t('workItemCreator.actualBehavior')}</label>
                        <textarea
                          value={formData.actualBehavior}
                          onChange={(e) => setFormData((prev) => ({ ...prev, actualBehavior: e.target.value }))}
                          placeholder={t('workItemCreator.actualBehaviorPlaceholder')}
                          rows={2}
                          className={devopsTextareaCls}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={devopsLabelCls}>{t('workItemCreator.severity')}</label>
                        <select
                          value={formData.severity}
                          onChange={(e) => setFormData((prev) => ({ ...prev, severity: e.target.value }))}
                          className={devopsSelectCls}
                        >
                          <option value="">{t('workItemCreator.selectSeverity')}</option>
                          <option value="critical">{t('workItemCreator.riskLevels.critical')}</option>
                          <option value="high">{t('workItemCreator.riskLevels.high')}</option>
                          <option value="medium">{t('workItemCreator.riskLevels.medium')}</option>
                          <option value="low">{t('workItemCreator.riskLevels.low')}</option>
                        </select>
                      </div>
                      <div>
                        <label className={devopsLabelCls}>{t('workItemCreator.environment')}</label>
                        <input
                          type="text"
                          value={formData.environment}
                          onChange={(e) => setFormData((prev) => ({ ...prev, environment: e.target.value }))}
                          placeholder={t('workItemCreator.environmentPlaceholder')}
                          className={devopsInputCls}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Properties Sidebar */}
              <div className="w-[260px] flex-shrink-0 border-l border-border bg-bg space-y-0">
                <div className="px-3 py-1.5 bg-surface-hover border-b border-border">
                  <h3 className="text-[11px] font-bold text-text-secondary uppercase tracking-wider">{t('workItemCreator.properties')}</h3>
                </div>

                <div className="p-3 space-y-3">
                  {/* Parent Selection */}
                  {requiresParent && (
                    <div>
                      <label className={devopsLabelCls}>
                        {requiredParentLabel}
                      </label>
                      <SearchableDropdown
                        value={formData.selectedParentId}
                        options={parentSelectOptions}
                        onChange={(value) => setFormData((prev) => ({ ...prev, selectedParentId: value }))}
                        placeholder={t('workItemCreator.selectParent', { type: requiredParentLabel.toLowerCase() })}
                        searchPlaceholder={t('workItemCreator.searchParent')}
                        menuTitle={t('workItemCreator.selectParent', { type: requiredParentLabel })}
                        className={`${devopsSelectCls} disabled:bg-surface-hover disabled:cursor-not-allowed`}
                        dropdownClassName="w-[300px]"
                      />
                      {formData.projectId && potentialParents.length === 0 && (
                        <p className="text-[10px] text-amber-600 mt-0.5 flex items-center gap-1">
                          <span>⚠️</span>
                          {t('workItemCreator.noParentsFound', { type: requiredParentLabel.toLowerCase() })}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Sprint */}
                  <div>
                    <label className={devopsLabelCls}>{t('backlogPage.columns.sprint')}</label>
                    <SearchableDropdown
                      value={formData.sprintId}
                      options={sprintSelectOptions}
                      onChange={(value) => setFormData((prev) => ({ ...prev, sprintId: value }))}
                      disabled={!formData.projectId}
                      placeholder={t('workItemCreator.backlog')}
                      searchPlaceholder={t('workItemCreator.searchSprint')}
                      menuTitle={t('backlogPage.selectSprint')}
                      allowClear
                      className={`${devopsSelectCls} disabled:bg-surface-hover disabled:cursor-not-allowed`}
                      dropdownClassName="w-[280px]"
                    />
                  </div>

                  {/* Assignee */}
                  <div>
                    <label className={devopsLabelCls}>{t('backlogPage.columns.assignedTo')}</label>
                    <SearchableDropdown
                      value={formData.assigneeId}
                      options={assigneeSelectOptions}
                      onChange={(value) => setFormData((prev) => ({ ...prev, assigneeId: value }))}
                      placeholder={t('backlogPage.row.unassigned')}
                      searchPlaceholder={t('workItemCreator.searchAssignee')}
                      menuTitle={t('workItemCreator.assignWorkItem')}
                      allowClear
                      className={devopsSelectCls}
                      dropdownClassName="w-[280px]"
                    />
                  </div>

                  <div className="border-t border-border pt-3">
                    <h4 className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-2">{t('backlogPage.columns.status')}</h4>

                    {/* State */}
                    <div className="mb-2">
                      <label className={devopsLabelCls}>{t('workItemCreator.state')}</label>
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full shrink-0 ${STATUS_COLORS[formData.status] || 'bg-gray-400'}`} />
                        <SearchableDropdown
                          value={formData.status}
                          options={statusSelectOptions}
                          onChange={(value) => setFormData((prev) => ({ ...prev, status: value as TaskStatus }))}
                          placeholder={t('workItemCreator.selectState')}
                          searchPlaceholder={t('workItemCreator.searchState')}
                          menuTitle={t('workItemCreator.selectState')}
                          className="flex-1 text-[12px] font-medium bg-transparent border border-border rounded-[3px] px-2 py-1 outline-none cursor-pointer text-text-primary"
                          dropdownClassName="w-[220px]"
                        />
                      </div>
                    </div>

                    {/* Priority */}
                    <div className="mb-2">
                      <label className={devopsLabelCls}>{t('backlogPage.columns.priority')}</label>
                      <SearchableDropdown
                        value={formData.priority}
                        options={prioritySelectOptions}
                        onChange={(value) => setFormData((prev) => ({ ...prev, priority: value as TaskPriority }))}
                        placeholder={t('workItemCreator.selectPriority')}
                        searchPlaceholder={t('workItemCreator.searchPriority')}
                        menuTitle={t('workItemCreator.selectPriority')}
                        className="w-full text-[12px] font-medium bg-transparent border border-border rounded-[3px] px-2 py-1 outline-none cursor-pointer text-text-primary"
                        dropdownClassName="w-[220px]"
                      />
                    </div>

                    {/* Due Date */}
                    <div className="mb-2">
                      <label className={devopsLabelCls}>{t('backlogPage.columns.dueDate')}</label>
                      <div className="flex flex-col gap-0.5">
                        <input
                          type="date"
                          value={formData.dueDate}
                          onChange={(e) => setFormData((prev) => ({ ...prev, dueDate: e.target.value }))}
                          className="px-1.5 py-0.5 border border-border rounded-[3px] text-[11px] text-text-primary bg-surface hover:border-brand focus:border-brand focus:outline-none transition-colors w-full"
                        />
                        <span className="text-[10px] text-gray-400">{browserTimeZone}</span>
                      </div>
                    </div>

                    {/* Est. Hours - Only for Tasks and Bugs */}
                    {(formData.type === WorkItemType.TASK || formData.type === WorkItemType.BUG) && (
                      <div className="mb-2">
                        <label className={devopsLabelCls}>{t('workItemCreator.estimatedHours')}</label>
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          value={formData.estimatedHours}
                          onChange={(e) => setFormData((prev) => ({ ...prev, estimatedHours: e.target.value }))}
                          placeholder="—"
                          className="px-1.5 py-0.5 border border-border rounded-[3px] text-[11px] text-text-primary bg-surface hover:border-brand focus:border-brand focus:outline-none transition-colors w-full"
                        />
                      </div>
                    )}
                  </div>

                  <div className="border-t border-border pt-3">
                    <h4 className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-2">{t('workItemCreator.details')}</h4>

                    {/* Tags */}
                    <div className="mb-2">
                      <label className={devopsLabelCls}>{t('backlogPage.columns.tags')}</label>
                      <input
                        type="text"
                        value={formData.tags}
                        onChange={(e) => setFormData((prev) => ({ ...prev, tags: e.target.value }))}
                        placeholder={t('workItemCreator.tagsPlaceholder')}
                        className="px-1.5 py-0.5 border border-border rounded-[3px] text-[11px] text-text-primary bg-surface hover:border-brand focus:border-brand focus:outline-none transition-colors w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div className="flex items-center justify-between px-5 py-2.5 border-t border-border bg-surface flex-shrink-0">
          <span className="text-[11px] text-text-muted">
            {!isFormValid && t(parentId ? 'workItemCreator.fillRequiredWithParent' : 'workItemCreator.fillRequired')}
          </span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className={actionBtnOutline}>
              <FiX className={actionBtnIconClass} aria-hidden />
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={!isFormValid || isSaving}
              className={actionBtnPrimary}
            >
              <FiPlus className={actionBtnIconClass} aria-hidden />
              {isSaving ? t('workItemCreator.creating') : t('workItemCreator.createType', { type: workItemLabel(formData.type).toLowerCase() })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(creator, document.body);
}